import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  outlinedButtonSx,
  primaryButtonSx,
} from "../../../Organization/CodeSecurity/codeSecurityShared";
import {
  DOMAIN_STAGES,
  DOMAIN_STATUSES,
  customUrl,
  normalizeDomain,
  validateCustomDomain,
} from "../../data/publishing";
import { DnsRecord } from "../components/PublishDialog";
import { Section } from "./Section";

const Row = ({ label, help, children }) => {
  const theme = useTheme();
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={{ xs: 1, sm: 2 }}
      alignItems={{ sm: "flex-start" }}
      sx={{ py: 1.5 }}
    >
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={{ width: { sm: 170 }, flexShrink: 0, minHeight: 36 }}
      >
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
        <Tooltip title={help} arrow>
          <InfoOutlinedIcon
            aria-label={help}
            sx={{ fontSize: 16, color: theme.customColors.icons.secondary }}
          />
        </Tooltip>
      </Stack>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 36,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        {children}
      </Box>
    </Stack>
  );
};

const StatusPill = ({ status, label }) => {
  const theme = useTheme();
  const colors = {
    unknown: theme.customColors.text.disabled,
    pending: theme.customColors.status.warning.main,
    misconfigured: theme.customColors.status.error.main,
    active: theme.customColors.status.success.main,
  };
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        px: 1.5,
        py: 0.75,
        borderRadius: "8px",
        border: `1px solid ${theme.customColors.border.main}`,
        textTransform: "uppercase",
        fontSize: 13,
        fontWeight: 600,
        letterSpacing: 0.5,
        color: theme.customColors.text.secondary,
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: colors[status],
        }}
      />
      {label || DOMAIN_STATUSES[status].label}
    </Box>
  );
};

// One prompt for an AI assistant, with this domain and target filled in, so
// a customer gets the exact clicks for their DNS provider. What they tick
// (Cloudflare, CAA records) is added to it. Opens in ChatGPT or can be
// copied into any assistant.
export const dnsHelpPrompt = (
  domain,
  target,
  { cloudflare = false, caa = false, baseDomain = "" } = {}
) =>
  [
    `I need to add this DNS record so ${domain} points to my Trust Center:`,
    `- Type: CNAME`,
    `- Name: ${domain}`,
    `- Target: ${target}`,
    ...(cloudflare
      ? [`- Proxy status: DNS only (grey cloud), not Proxied`, `- TTL: Auto`]
      : []),
    cloudflare
      ? `My DNS is managed in Cloudflare. Walk me through adding this record step by step in the Cloudflare dashboard. If a record for ${domain} already exists, tell me how to edit it instead.`
      : `Ask me which DNS provider I use (for example GoDaddy, Cloudflare, Route 53, Namecheap or Google), then walk me through adding the record there step by step. If a record for ${domain} already exists, tell me how to change it instead.`,
    // CAA is looked up on the name and every parent, so the company's own
    // domain (acme.com) is where a record usually is.
    ...(caa
      ? [
          `My domain may have CAA records. Show me how to check whether ${baseDomain} or ${domain} has CAA records (with dig, nslookup or an online tool). If it does, walk me through adding one more record that allows Amazon to issue the certificate, without removing the ones I already have:`,
          `- Name: ${baseDomain}`,
          `- Type: CAA`,
          `- Value: 0 issue "amazon.com"`,
          `If there are no CAA records, tell me nothing needs to change.`,
        ]
      : []),
    `Finally, tell me how to check that ${domain} points to ${target}.`,
  ].join("\n");

// ChatGPT opens a new chat with the prompt already typed in.
export const chatGptLink = (prompt) =>
  `https://chatgpt.com/?q=${encodeURIComponent(prompt)}`;

// A tick box for a case that needs one more thing done; ticked, it says
// what and why (the steps are in the one prompt below).
const HelpChoice = ({ label, region, rule, why, checked, onChange }) => {
  const theme = useTheme();
  return (
    <Box>
      <FormControlLabel
        sx={{ ml: -1, mr: 0 }}
        control={
          <Checkbox
            size="small"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
          />
        }
        label={<Typography variant="body2">{label}</Typography>}
      />
      {checked && (
        <Box
          role="region"
          aria-label={region}
          sx={{
            mb: 1,
            ml: 3.5,
            pl: 1.5,
            borderLeft: `2px solid ${theme.customColors.border.main}`,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {rule}
          </Typography>
          <Typography
            variant="caption"
            sx={{ display: "block", color: theme.customColors.text.secondary }}
          >
            {why}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// Under the DNS record: the two cases that need one more step, then one
// prompt (with whatever is ticked) to copy or open in ChatGPT.
const DnsHelp = ({ domain, target, baseDomain }) => {
  const theme = useTheme();
  const [cloudflare, setCloudflare] = useState(false);
  const [caa, setCaa] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [copied, setCopied] = useState(false);
  const prompt = dnsHelpPrompt(domain, target, { cloudflare, caa, baseDomain });
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  return (
    <Box sx={{ mt: 1 }}>
      <HelpChoice
        label="My DNS is on Cloudflare"
        region="Cloudflare help"
        rule="Set the record to DNS only (grey cloud), not Proxied."
        why="Proxied hides the record, so we cannot see it and the secure certificate cannot be issued."
        checked={cloudflare}
        onChange={setCloudflare}
      />
      <HelpChoice
        label="My domain has CAA records (or I am not sure)"
        region="CAA help"
        rule={`Allow amazon.com in your CAA records on ${baseDomain}.`}
        why="CAA records say which companies may issue certificates for your domain. If they do not include Amazon, the secure certificate for your Trust Center cannot be issued. With no CAA records at all, nothing needs to change."
        checked={caa}
        onChange={setCaa}
      />
      <Box
        role="group"
        aria-label="Help adding the record"
        sx={{
          mt: 1,
          p: 1.5,
          borderRadius: "8px",
          border: `1px solid ${theme.customColors.border.main}`,
        }}
      >
        <Typography
          variant="caption"
          sx={{ display: "block", color: theme.customColors.text.secondary }}
        >
          Not sure how to add or change it? Ask an AI assistant for the steps at
          your DNS provider. The prompt includes what you ticked above.
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ mt: 1, flexWrap: "wrap", rowGap: 1 }}
        >
          <Button
            size="small"
            variant="outlined"
            component="a"
            href={chatGptLink(prompt)}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
            sx={outlinedButtonSx(theme)}
          >
            Ask ChatGPT
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
            onClick={copy}
            sx={outlinedButtonSx(theme)}
          >
            {copied ? "Copied" : "Copy prompt"}
          </Button>
          <Link
            component="button"
            type="button"
            variant="caption"
            underline="hover"
            onClick={() => setShowPrompt(!showPrompt)}
          >
            {showPrompt ? "Hide prompt" : "See the prompt"}
          </Link>
        </Stack>
        {showPrompt && (
          <Box
            component="pre"
            aria-label="Prompt for DNS help"
            sx={{
              m: 0,
              mt: 1,
              p: 1.25,
              borderRadius: "6px",
              backgroundColor: theme.customColors.background.light,
              fontFamily: "inherit",
              fontSize: 12,
              lineHeight: 1.5,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {prompt}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// "Custom domain" section of the Trust Center settings. Changes apply at once
// (no Save), like ComplyJet's Custom Domain card: the server saves the
// domain, looks its DNS record up and keeps the status. Each action
// resolves to true when it worked (the page shows the server's message
// otherwise).
const CustomDomainPanel = ({
  data,
  regoditLink,
  onConnect,
  onCheck,
  onRemove,
}) => {
  const theme = useTheme();
  const { publishing } = data;
  const domain = publishing.customDomain;
  const status = publishing.customDomainStatus || "unknown";
  const target = publishing.cnameTarget;
  // CloudFront progress (null when the API only checks DNS).
  const stage = DOMAIN_STAGES[publishing.customDomainStage] || null;
  const help =
    publishing.customDomainStage === "failed"
      ? publishing.customDomainError || stage.help
      : stage?.help || DOMAIN_STATUSES[status].help;
  const [input, setInput] = useState("");
  const [error, setError] = useState("");
  // "connect" | "check" | "remove" while that call runs.
  const [busy, setBusy] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => setConfirmDelete(false), [domain]);

  const run = async (what, action) => {
    setBusy(what);
    const ok = await action();
    setBusy(null);
    return ok;
  };

  const connect = async () => {
    const problem = validateCustomDomain(input, data.company?.domain || "");
    if (problem) {
      setError(problem);
      return;
    }
    if (await run("connect", () => onConnect(normalizeDomain(input)))) {
      setInput("");
      setError("");
    }
  };

  const checkStatus = () => run("check", onCheck);

  const remove = () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    run("remove", onRemove);
  };

  return (
    <Section
      title="Custom domain"
      subtitle="Connect your own domain and open your Trust Center there."
    >
      <Box>
        <Row
          label="Base domain"
          help="Your Trust Center always works at this Regodit link, even with a custom domain."
        >
          <Link
            href={regoditLink}
            target="_blank"
            rel="noopener"
            variant="body2"
            sx={{ wordBreak: "break-all" }}
          >
            {regoditLink.replace(/^https?:\/\//, "")}
          </Link>
        </Row>

        <Row
          label="Custom domain"
          help="A subdomain your company owns, like trust.yourcompany.com."
        >
          {domain ? (
            <Link
              href={customUrl(domain)}
              target="_blank"
              rel="noopener"
              variant="body2"
              sx={{ wordBreak: "break-all" }}
            >
              {domain}
            </Link>
          ) : (
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                size="small"
                fullWidth
                label="Your domain"
                placeholder="trust.yourcompany.com"
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  setError("");
                }}
                onKeyDown={(e) => e.key === "Enter" && connect()}
                error={!!error}
                helperText={error}
              />
              <Button
                variant="contained"
                onClick={connect}
                disabled={busy === "connect"}
                sx={{ ...primaryButtonSx(theme), py: 0.9, flexShrink: 0 }}
              >
                {busy === "connect" ? "Connecting…" : "Connect"}
              </Button>
            </Stack>
          )}
        </Row>

        {domain && (
          <>
            <Row
              label="Status"
              help="Whether we found the DNS record that points your domain to us."
            >
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ flexWrap: "wrap", rowGap: 1 }}
              >
                <StatusPill status={status} label={stage?.label} />
                <Button
                  variant="outlined"
                  onClick={checkStatus}
                  disabled={!!busy}
                  startIcon={
                    busy === "check" ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : null
                  }
                  sx={outlinedButtonSx(theme)}
                >
                  {busy === "check" ? "Checking…" : "Check status"}
                </Button>
                {publishing.customDomainCheckedAt && (
                  <Typography
                    variant="caption"
                    sx={{ color: theme.customColors.text.secondary }}
                  >
                    Checked{" "}
                    {new Date(publishing.customDomainCheckedAt).toLocaleString(
                      undefined,
                      { dateStyle: "medium", timeStyle: "short" }
                    )}
                  </Typography>
                )}
              </Stack>
              <Typography
                variant="caption"
                sx={{
                  mt: 0.75,
                  color:
                    status === "misconfigured"
                      ? theme.customColors.status.error.text
                      : theme.customColors.text.secondary,
                }}
              >
                {help}
                {status !== "active" && publishing.customDomainStage && (
                  <>
                    {" "}
                    We check again every few minutes and email you when it is
                    live.
                  </>
                )}
              </Typography>
              {status !== "active" && (
                <>
                  <DnsRecord domain={domain} target={target} />
                  <DnsHelp
                    domain={domain}
                    target={target}
                    baseDomain={
                      normalizeDomain(data.company?.domain || "").replace(
                        /^www\./,
                        ""
                      ) || domain.split(".").slice(1).join(".")
                    }
                  />
                </>
              )}
            </Row>

            <Row
              label="Actions"
              help="Removing the custom domain keeps your Trust Center live at the base domain."
            >
              <Box>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={remove}
                  disabled={busy === "remove"}
                  sx={{ borderRadius: "8px", textTransform: "none" }}
                >
                  {confirmDelete ? "Yes, delete it" : "Delete custom domain"}
                </Button>
                {confirmDelete && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mt: 0.75,
                      fontStyle: "italic",
                      color: theme.customColors.text.secondary,
                    }}
                  >
                    Click again to delete. Your page stays live at the base
                    domain.
                  </Typography>
                )}
              </Box>
            </Row>
          </>
        )}
      </Box>
    </Section>
  );
};

export default CustomDomainPanel;
